import React, { Suspense, lazy } from 'react';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import reduxConnectContainer from './FeaturedDeviation.container';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import PremiumFolderDataIcon from '@wix/da-shared-react/pkg/GallectionFolderDropdown/_partials/PremiumFolderDataIcon';
import { isDeviationAuthor } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import WidgetBox, { Props as WidgetBoxProps } from '../_partials/WidgetBox';
import FavouritesLabel from '../_partials/FavouritesLabel';
import {
  default as FeaturedDeviation,
  Props as WidgetProps,
} from './FeaturedDeviation';
import EmptyState from '../../EmptyState';
import ChunkLoadingPopover from '../_partials/ChunkLoadingPopover';

import s from './FeaturedDeviation.scss';

const DeviationPicker = lazy(() =>
  import(
    /* webpackChunkName: "owner" */ './EditForm/DeviationPicker/DeviationPicker.connected'
  )
);

const EditForm = lazy(() =>
  import(/* webpackChunkName: "owner" */ './EditForm')
);

const enhance = compose(reduxConnectContainer, withTranslation());
export interface State {
  showDeviationPicker: boolean;
}
class FeaturedDeviationBox extends React.Component<
  WidgetProps & WidgetBoxProps & WithTranslation,
  State
> {
  state: State = {
    showDeviationPicker: false,
  };
  render() {
    const {
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      section,
      zone,
      ...props
    } = this.props;

    const isDeviationUnavailable =
      !props.deviation ||
      (props.deviation.isBlocked && !props.deviation.isMature) || // Mature should show the Dumbo (which supports hidden mature mode)
      props.deviation.isDeleted;

    const emptyStateView = 'large';
    const emptyStateClassName = s['empty-state'];

    return (
      <>
        {this.state.showDeviationPicker &&
          this.renderDeviationPicker(this.hideDeviationPicker)}
        <WidgetBox
          section={section}
          zone={zone}
          moduleId={props.moduleId}
          header={
            <>
              <PremiumFolderDataIcon
                premiumFolderData={props?.deviation?.premiumFolderData}
                size={IconSize.COMPACT}
                className={s['premium-folder-icon']}
              />
              {props.t(props.title || 'widgets.featured_deviation.header')}
            </>
          }
          headerExtras={this.renderHeaderExtras()}
          autoShowWidgetConfig={true}
          widgetMenuRenderer={this.renderConfigPopup}
          widgetMoveUp={widgetMoveUp}
          widgetMoveDown={widgetMoveDown}
          widgetRemove={widgetRemove}
          isEmpty={!props.deviation}
          emptyView={
            props.isOwner && (
              <EmptyState
                view={emptyStateView}
                className={emptyStateClassName}
                title={props.t('widgets.featured_deviation.empty.title.owner')}
                subtitle={props.t(
                  'widgets.featured_deviation.empty.subtitle.owner'
                )}
                withButton
                buttonLabel={props.t(
                  'widgets.featured_deviation.empty.CTALabel.owner'
                )}
                onButtonClick={this.showDeviationPicker}
              />
            )
          }
        >
          {isDeviationUnavailable && props.isOwner && (
            <EmptyState
              view={emptyStateView}
              className={emptyStateClassName}
              title={props.t(
                'widgets.featured_deviation.unavailable.title.owner'
              )}
              subtitle={props.t(
                'widgets.featured_deviation.unavailable.subtitle.owner'
              )}
              withButton
              buttonLabel={props.t(
                'widgets.featured_deviation.empty.CTALabel.owner'
              )}
              onButtonClick={this.showDeviationPicker}
            />
          )}
          {isDeviationUnavailable && !props.isOwner && (
            <EmptyState
              view={emptyStateView}
              className={emptyStateClassName}
              title={props.t(
                'widgets.featured_deviation.unavailable.title.visitor'
              )}
              subtitle={props.t(
                'widgets.featured_deviation.unavailable.subtitle.visitor'
              )}
            />
          )}
          {!isDeviationUnavailable && <FeaturedDeviation {...props} />}
        </WidgetBox>
      </>
    );
  }

  renderHeaderExtras = () => {
    const { deviation, profileOwnerUser } = this.props;
    if (deviation && !isDeviationAuthor(deviation, profileOwnerUser)) {
      return <FavouritesLabel tooltipUser={profileOwnerUser} />;
    }
  };

  renderConfigPopup = close => {
    const { moduleId, isNewlyAddedModule } = this.props;
    if (isNewlyAddedModule) {
      return this.renderDeviationPicker(close);
    }
    return (
      <Suspense fallback={<ChunkLoadingPopover />}>
        <EditForm moduleId={moduleId} onClose={close} {...this.props} />
      </Suspense>
    );
  };

  renderDeviationPicker = (close?) => {
    return (
      <Suspense fallback={<ChunkLoadingPopover />}>
        <DeviationPicker moduleId={this.props.moduleId} onClose={close} />
      </Suspense>
    );
  };

  showDeviationPicker = () => {
    this.setState({ showDeviationPicker: true });
  };
  hideDeviationPicker = () => {
    this.setState({ showDeviationPicker: false });
  };
}

export default enhance(FeaturedDeviationBox) as React.ComponentType<{}>;
